<script setup>
import { computed, watch } from 'vue';
import { router } from '@inertiajs/vue3';
import { useMemberAssessmentStore } from '@/stores/memberAssessment';
import { useOptionsStore } from '@/stores/options';
import { storeToRefs } from 'pinia';
import { useToast } from 'vue-toastification';
import { trans } from 'laravel-vue-i18n';

const props = defineProps({
    assessmentHash: {
        type: String,
        required: true,
    },
});

const toast = useToast();
const store = useMemberAssessmentStore();
const options = useOptionsStore();

const { scaledOptions } = storeToRefs(options);
const {
    currentAssessment,
    flowQuestions: questions,
    flowQuestionIndex: index,
    loadingFlowQuestions: loadingQuestions,
} = storeToRefs(store);

// Current assessment for the page
const pageAssessment = computed(() => {
    if (currentAssessment.value?.hash !== props.assessmentHash) {
        return null;
    }

    return currentAssessment.value;
});

const scaledDisplayOptions = computed(() => {
    const total = scaledOptions.value.length;
    return scaledOptions.value.map((opt, idx) => ({
        id: opt.value,
        label: opt.label,
        display: total - idx,
    }));
});

const allowEdit = computed(() =>
    pageAssessment.value?.can_edit
);

// Load Qns
watch(() => props.assessmentHash, async (hash) => {
    await store.loadAssessmentFlow(hash);
}, { immediate: true });

// completion %
const completion = computed(() => {
    if (!questions.value.length) {
        return 0;
    }

    let answered = 0;
    questions.value.forEach(q => {
        const r = q.responses[0];
        if (r.scaled_response_id || r.short_answer) {
            answered++;
        }
    });

    return Math.round((answered / questions.value.length) * 100);
});

const saveAllResponses = async () => {
    if (!allowEdit.value) {
        return;
    }

    return store.saveQuestionsResponses({
        questions_responses: questions.value,
        completion_percentage: completion.value,
        hash: props.assessmentHash
    });
};

const save = async () => {
    await saveAllResponses();
    toast.success(trans('label.saved_success'));
};

const submitAssessment = async () => {
    try {
        // Save before submitting
        await saveAllResponses();

        if (completion.value < 100) {
            toast.error(trans('label.complete_all_questions'));
            return;
        }

        // Submit
        const response = await store.submit({ hash: props.assessmentHash });

        toast.success(trans('label.assessment_submitted'));

        if (response?.generation_started) {
            toast.info('Profile generation started..');
        }

        router.visit('/member-assessments');

    } catch (error) {
        const message = error?.response?.data?.message || trans('label.assessment_not_complete_or_submitted');
        toast.error(message);
    }
};
const questionTitle = computed(() => {
    if (!questions.value.length) {
        return trans('label.question_of', { current: 0, total: 0 });
    }

    return trans('label.question_of', {
        current: index.value + 1,
        total: questions.value.length
    });
});

const percentComplete = computed(() =>
    trans('label.complete_short', { percent: completion.value + '%' })
);

const next = () => {
    if (index.value < questions.value.length - 1) {
        index.value++;
    } 
};
const back = () => {
    if (index.value > 0) {
        index.value--;
    } 
};
</script>
<template>
<MainContentLayout>

    <div class="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div class="mb-8">
            <h1 class="text-2xl sm:text-3xl font-bold text-gray-900">
                {{ pageAssessment?.assessment?.name || pageAssessment?.domain?.name }}
            </h1>

            <div class="flex justify-between items-center mt-3">
                <span class="text-sm sm:text-base md:text-lg font-medium text-primary/80">
                    {{ questionTitle }}
                </span>
                <span class="text-sm sm:text-base md:text-lg font-medium text-primary/80">
                    {{ percentComplete }}
                </span>
            </div>

            <div class="w-full bg-gray-200 h-1.5 rounded-full mt-2">
                <div
                    class="bg-primary h-1.5 rounded-full transition-all duration-300"
                    :style="{ width: completion + '%' }" />
            </div>
        </div>

        <div
            v-if="loadingQuestions"
            class="rounded-2xl bg-white p-6 sm:p-10 shadow-md shadow-neutral-300/20 text-center text-gray-500">
            {{ $t('label.loading...') }}
        </div>

        <div
            v-else
            class="rounded-2xl bg-white p-6 sm:p-10 shadow-md shadow-neutral-300/20">
            <p class="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 leading-normal text-left mb-8">
                {{ questions[index]?.question_text }}
            </p>

            <!-- Scaled Response Questions -->
            <div v-if="questions[index]?.type?.name === 'Scaled'" class="w-full">
                <div class="flex justify-between text-xs sm:text-sm md:text-base text-gray-700 mb-6 px-2 sm:px-4 font-medium">
                    <span>{{ scaledDisplayOptions[0]?.label }}</span>
                    <span>{{ scaledDisplayOptions[scaledDisplayOptions.length - 1]?.label }}</span>
                </div>
                <div class="grid grid-cols-5 gap-3 sm:gap-6 place-items-center">
                    <template v-for="opt in scaledDisplayOptions" :key="opt.id">
                        <div class="flex flex-col items-center h-full justify-start">
                            <button
                                class="rounded-full aspect-square w-10 sm:w-14 md:w-16 flex items-center justify-center text-base sm:text-lg md:text-xl font-semibold transition-all border"
                                :class="questions[index].responses[0].scaled_response_id === opt.id
                                    ? 'bg-primary text-white border-primary shadow-md scale-105'
                                    : 'bg-white text-gray-700 border-gray-300 hover:border-primary hover:text-primary'"
                                :disabled="!allowEdit"
                                @click="questions[index].responses[0].scaled_response_id = opt.id">
                                {{ opt.display }}
                            </button>
                            <span class="text-[0.70rem] sm:text-xs md:text-sm lg:text-base text-gray-700 mt-3 font-medium text-center leading-snug">
                                {{ opt.label }}
                            </span>
                        </div>
                    </template>
                </div>
            </div>

            <!-- Short Answer Questions -->
            <div v-if="questions[index]?.type?.name === 'Short Answer'" class="mt-6">
                <StyledTextArea
                    v-model="questions[index].responses[0].short_answer"
                    :disabled="!allowEdit" />
            </div>

        </div>

        <div class="flex justify-center gap-4 sm:gap-6 mt-10 flex-wrap">
            <StyledButton
                :disabled="index === 0"
                class="min-w-[90px]"
                @click="back">
                {{ $t('label.back') }}
            </StyledButton>

            <StyledButton
                v-if="index < questions.length - 1"
                type="primary"
                class="min-w-[90px]"
                @click="next">
                {{ $t('label.next') }}
            </StyledButton>

            <StyledButton
                v-if="allowEdit"
                class="min-w-[90px]"
                @click="save">
                {{ $t('label.save') }}
            </StyledButton>

            <StyledButton
                v-if="allowEdit && index === questions.length - 1"
                type="primary"
                class="min-w-[130px]"
                :disabled="completion < 100"
                @click="submitAssessment">
                {{ $t('label.submit') }}
            </StyledButton>
        </div>
    </div>
</MainContentLayout>
</template>
